/**
 * Pre-compare confirmation copy — ALL user-visible strings for the
 * pre-compare confirmation modal (fu4 sub-task 13, Trello #748) live in THIS
 * ONE MODULE, same discipline as `conflictActionsCopy.ts` (kept as a sibling
 * module so sub-task 11's parallel edits there never collide with this one).
 *
 * All copy in English (app-content language rule).
 */

import type { ComparePhase } from "@nu-assist/contracts";

export const PRE_COMPARE_TITLE = "Compare to Thrive";

export const PRE_COMPARE_QUESTION = "What do you want to compare?";

/** The heading when there IS no question — the selection covers everything the
 *  run would compare, so the modal states what will happen instead of asking. */
export const PRE_COMPARE_STATEMENT = "This is what will run.";

/** Radio option when a selection exists — `n` is the pending count the run
 *  will actually enqueue (already-exported rows are excluded by
 *  `computeCompareScope`, so the number here can be smaller than the raw
 *  selection; the modal must never advertise a count the run won't honour). */
export function preCompareSelectedLabel(n: number): string {
  return n === 1 ? "Compare the 1 selected contact" : `Compare the ${n} selected contacts`;
}

/** Radio option / single statement for the whole import. `n` is what the run
 *  will enqueue, NOT the import's total: a whole-import run narrows to the
 *  not-yet-exported contacts, so on a partially-exported import this reads
 *  lower than the row count on screen. Same rule as the selection label above
 *  — the modal must never advertise a count the run won't honour. */
export function preCompareAllLabel(n: number): string {
  return n === 1 ? "Compare all 1 contact" : `Compare all ${n} contacts`;
}

/**
 * The whole-import option while the exact number is UNAVAILABLE — before the
 * unfiltered `counts.review` fetch lands, and permanently if it failed.
 *
 * No number, deliberately. The page can compute one (`totalContacts −
 * exportedCount`) but it is blind to DISMISSALS: neither `ImportDetail` nor
 * `ExportStatus` carries a dismissal count, so `computeCompareScope`'s
 * whole-import arm hardcodes `ignored: 0` — while the run's scope query drops
 * those rows server-side (`left_out_at IS NULL` in both
 * `list_contacts_for_compare` twins). On an import with 3 dismissed of 15 the
 * blind number says 15 for a run that compares 12.
 *
 * That is the iter-1 invariant — never advertise a count the run won't honour —
 * on a second axis, and the exact number genuinely does not exist on this
 * screen until the counts arrive. So the option says what it will do without
 * claiming how many. The time estimate beside it still uses the blind number:
 * it is an UPPER bound, and over-promising time is the safe direction for a
 * promise (same reasoning as `fallbackShare`'s loading ceiling).
 */
export const PRE_COMPARE_ALL_UNKNOWN_LABEL = "Compare all remaining contacts";

/**
 * What the scope's number LEAVES OUT, or `null` when it leaves out nothing.
 *
 * The requirement (operator, 2026-09-07, from hand-testing): a 15-contact
 * import offering "Compare all 7 contacts" must never leave the other 8
 * unaccounted for. The number itself is correct — this line is what makes it
 * legible without a database query.
 *
 * "Skipping N …" rather than "N … are skipped" so the sentence stays
 * grammatical at every combination: one clause, two clauses, and a count of 1
 * all read the same way without a singular/plural verb to agree with.
 *
 * THE SECOND CLAUSE SAYS "skipped", NOT "ignored" (fu5 sub-task 02). It was the
 * last recruiter-visible "ignored" string in the web app: sub-task 01 renamed
 * the contacts table, 02 renamed the modal, the pill, the tab, the button and
 * the export panel, and this line — on a DIFFERENT modal — was missed by both.
 * A recruiter reading "Skipping 8 already exported and 2 ignored" here and then
 * meeting a `Skipped` tab two clicks later is being shown one state under two
 * names.
 *
 * The PARAMETER keeps its `ignored` name on purpose: it carries the
 * `review_status` WIRE VALUE `"ignored"`, and renaming the identifier would put
 * the code one word away from the column it counts, for no reader's benefit.
 * Same rule `formatIgnoredDate` follows in `conflictActionsCopy.ts`.
 */
export function preCompareSkippedLine(alreadyExported: number, ignored: number): string | null {
  const clauses: string[] = [];
  if (alreadyExported > 0) clauses.push(`${alreadyExported} already exported`);
  if (ignored > 0) clauses.push(`${ignored} skipped`);
  if (clauses.length === 0) return null;
  return `Skipping ${clauses.join(" and ")}`;
}

/**
 * First half of the coverage nudge — contacts Compare has never classified.
 *
 * `n` is `counts.review.not_compared`, which is a LOWER BOUND on the
 * never-classified population by its own contract (`schemas.py`'s Field
 * description): a row exported or failed without ever being compared counts
 * under `exported` / `failed`, not here. Both callers render this as a nudge,
 * never as a guarantee — `ExportToThrivePanel`'s `coverageLine` docblock spells
 * out both directions of the imprecision and why it is documented rather than
 * mechanised. Read that before treating this sentence as a bound
 * (/pr-review iter 8, Agent C).
 */
export function preCompareNeverComparedLine(n: number): string {
  return n === 1 ? "1 contact was never compared" : `${n} contacts were never compared`;
}

/** Second half of the coverage nudge — verdicts older than 7 days. */
export function preCompareStaleLine(n: number): string {
  return `${n} compared more than a week ago`;
}

/** Separates the two coverage lines when both render. */
export const PRE_COMPARE_COVERAGE_SEPARATOR = " · ";

/**
 * The single statement when the selection and the whole import resolve to the
 * SAME SET, so there is nothing to choose between.
 *
 * The operator hit this with every row ticked: two radios reading "the 15
 * selected contacts" and "all 15 contacts" — the same number twice, one of
 * which had to be clicked. A choice between identical options is not a choice,
 * it is a step.
 *
 * "Everything", not a repeat of the count beside it: the number is already on
 * the line, and the word is the part the two old options were failing to say.
 */
export function preCompareEverythingLabel(n: number): string {
  return n === 1 ? "Compare this 1 contact" : `Compare all ${n} contacts`;
}

export const PRE_COMPARE_ESTIMATE_FOOTNOTE = "Estimated time is based on list size.";

export const PRE_COMPARE_CANCEL = "Cancel";

export const PRE_COMPARE_RUN = "Run compare";

/**
 * Why a selection is too large to compare, and what to do instead.
 *
 * Says all three things a recruiter needs: how many the run would cover, where
 * the limit is, and the way out. The way out is the important half — "Compare
 * all" sends no ids and therefore has no limit, so the answer is rarely "pick
 * fewer" and usually "you probably meant all of them".
 *
 * "left to compare", not "you selected": the number is the selection's PENDING
 * count, which is what the request carries — a recruiter who ticked 340 rows of
 * which 40 are already exported would otherwise be told they selected 300 and
 * be able to count the checkboxes and disagree.
 */
export function preCompareTooManySelected(selected: number, max: number): string {
  return `That selection has ${selected} contacts left to compare, and a selection can compare at most ${max} at a time. Compare all of them instead, which has no limit. Or untick some rows and run it in batches.`;
}

/**
 * What the compare bar says is happening right now, keyed by phase.
 *
 * `satisfies Record<ComparePhase, string | null>` is the THIRD leg of the phase
 * contract, and the one that was missing. `PROGRESS_PHASES` (python-shared) is
 * walked against `ComparePhase` (the api) by a round-trip test; nothing walked
 * either against this file, and nothing could — `compareProgressPhaseLabel`
 * takes a `string`, which erases the generated union, and a `switch` with a
 * `default` never fails `tsc` on a missing case. So adding a seventh phase went
 * red in python-shared, green once added to the api, and the web silently
 * rendered a count with NO LABEL — a frozen number and a blank caption, which
 * is exactly the "reads as a hung job" failure the per-phase design exists to
 * prevent (/pr-review iter 1, Agent C I3 / Agent A §2.3).
 *
 * With `satisfies`, a phase added to the contract and not to this map is a
 * compile error here.
 *
 * Phrased as what the app is checking, not as an internal phase name: the
 * recruiter has no model of "stragglers" or "the URL batch", and does have one
 * of "it is looking people up by their LinkedIn, then by email, then by name".
 *
 * `done` maps to `null` deliberately: the bar is hidden on a terminal run, and
 * a "Done" caption under a full bar in a modal that has already swapped to its
 * results would be a second, contradictory answer.
 */
const COMPARE_PHASE_LABELS = {
  linkedin_urls: "Checking LinkedIn URLs",
  linkedin_handles: "Checking LinkedIn profiles",
  emails: "Checking emails",
  names: "Checking names",
  finalizing: "Finishing up",
  done: null,
} satisfies Record<ComparePhase, string | null>;

/**
 * The label for a phase, or `null` when there is none to show.
 *
 * Takes a `string` rather than `ComparePhase` on purpose: the argument arrives
 * off the wire, and a worker deployed ahead of this bundle sends a value this
 * union does not contain. That is the case the fallback exists for — the api
 * already maps a phase IT does not know to `null`, and this handles the other
 * direction. The caller then shows the counts alone rather than inventing a
 * label.
 */
/**
 * The phases this map has an ENTRY for — not the phases it labels. `done` is
 * in here and its value is `null`, so a name promising labels would be wrong
 * about the one phase the map deliberately leaves unlabelled
 * (/pr-review iter 4, Agent C C-M1).
 *
 * Exported ONLY so the spec can compare the map against the contract in BOTH
 * directions. `satisfies` catches a phase in `ComparePhase` missing from the
 * map; nothing caught the reverse — a key left behind after a phase is removed
 * from the contract — because `satisfies` is the only leg that would see it
 * and no workflow runs `tsc` over `@nu-assist/web`. The orphan case was
 * editor-only, which is to say unenforced (iter 3, Agent C P3).
 */
export const PHASES_WITH_A_LABEL_ENTRY: readonly string[] = Object.keys(COMPARE_PHASE_LABELS);

export function compareProgressPhaseLabel(phase: string | null | undefined): string | null {
  if (phase == null) return null;
  // `Object.hasOwn`, not a bare index. The key comes off the wire, so a plain
  // lookup reaches the prototype: `labels["toString"]` is a FUNCTION, and `??`
  // does not catch it — a phase named `toString` or `constructor` would render
  // source code as the caption. The `switch` this map replaced had no such
  // hole; the refactor introduced it and its own new test caught it.
  if (!Object.hasOwn(COMPARE_PHASE_LABELS, phase)) return null;
  return (COMPARE_PHASE_LABELS as Record<string, string | null>)[phase] ?? null;
}
